import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Clock, DollarSign, Calendar, Activity } from "lucide-react";

const INCLUDED = [
  "Unlimited providers (Admin, Doctor, Nurse, Organizer)",
  "Unlimited patients on the mobile PWA",
  "40+ standard chemotherapy regimens pre-loaded",
  "187-drug oncology catalog with deduplication",
  "Treatment calendar generation + drag-drop reschedule",
  "Bidirectional messaging + SMS reminders (Twilio)",
  "ChemoAnalytics + Claude 4.5 AI insights",
  "Approval queue for reschedule / pause / refill / side-effect concerns",
  "Side-effect catalog with CTCAE-aligned severity tracking",
  "Provider wellness assessments",
  "Daily 24-hour SMS appointment reminder sweep",
  "PWA offline cache + service worker",
  "Email + SMS notifications via your SMTP + Twilio accounts",
  "Role-based access control + bcrypt + JWT",
  "Brute-force protection + password reset",
  "Full Python regression test suite + stamped PDF reports",
  "12 months of free updates",
  "Onboarding + 4 user-role training sessions",
];

export default function PricingPage() {
  // Interactive ROI calculator — oncology-specific framing
  const [hours, setHours]   = useState(22);   // hours saved per care team per week
  const [rate, setRate]     = useState(220);  // blended clinician + admin hourly rate
  const [teams, setTeams]   = useState(1);    // number of multi-disciplinary care teams

  const calc = useMemo(() => {
    const annualHours   = hours * teams * 52;
    const annualSavings = annualHours * rate;
    const monthlySaved  = annualSavings / 12;
    const payback       = monthlySaved > 0 ? (100000 / monthlySaved) : 0;
    return { annualHours, annualSavings, monthlySaved, payback };
  }, [hours, rate, teams]);

  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pt-16 pb-12 text-center">
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">Pricing</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl mx-auto">One license. Every role. Forever.</h1>
        <p className="text-slate-400 mt-4 max-w-2xl mx-auto">A single one-time fee covers every provider, every patient, and every future update for twelve months. No per-seat upcharges. No &quot;enterprise tier&quot; upsell.</p>
      </section>

      {/* Hero price card */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 pb-16">
        <div className="glass-card p-8 md:p-12 border-emerald-500/30 fade-up">
          <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-2">OncoCadence — full license</div>
              <div className="flex items-baseline gap-3 mb-3">
                <div className="text-6xl sm:text-7xl font-bold tracking-tighter tabular-nums">$100,000</div>
                <div className="text-slate-400 text-lg">one-time</div>
              </div>
              <p className="text-slate-400 max-w-md">Or split across <span className="text-emerald-400 font-bold">4 to 6 monthly installments</span> — no interest, no hidden fees. First installment unlocks production access.</p>
            </div>
            <Link to="/demo" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B1220] font-bold transition-colors shadow-2xl shadow-emerald-500/30 whitespace-nowrap" data-testid="pricing-demo-cta">
              Talk to sales <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
            {INCLUDED.map((line) => (
              <div key={line} className="flex items-start gap-2.5 text-sm">
                <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <span className="text-slate-300">{line}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installment grid */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 pb-20">
        <div className="text-center mb-8">
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">Installment options</div>
          <h2 className="text-3xl font-bold">Pay over four to six months.</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          <InstallmentCard months={4} monthly={25000} highlight />
          <InstallmentCard months={5} monthly={20000} />
          <InstallmentCard months={6} monthly={16667} />
        </div>
      </section>

      {/* ROI hero */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pb-12 text-center">
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">Return on investment</div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-3xl mx-auto">Pays for itself before cycle four.</h2>
        <p className="text-slate-400 mt-3 max-w-2xl mx-auto">An oncology care team spends 18-26 hours a week on scheduling, calendar reconciliation, patient call-backs, and dictation. OncoCadence removes that overhead — your team gets it back as clinic time.</p>
      </section>

      {/* Three KPI cards (matches the reference dashboard) */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 pb-10 grid md:grid-cols-3 gap-5">
        <KPICard color="emerald" label="Hours reclaimed" value="22" unit="hrs / week" caption="Per oncology care team. Scheduling, calendar reconciliation, patient call-backs, dictation, and no-show recovery combined." />
        <KPICard color="amber"   label="Clinician time saved" value="$251,680" unit="/ year" caption="At a blended clinician + admin rate of $220/hr. One team recovers 2.5× the licence cost in year one." />
        <KPICard color="violet"  label="Payback period" value="~4.8" unit="months" caption="$100,000 licence ÷ $20,973 monthly clinician-time saved. The licence pays for itself before the second cycle of treatment ends." />
      </section>

      {/* Where the hours go */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 pb-10">
        <div className="glass-card p-7">
          <h3 className="font-bold text-lg mb-1">Where the 22 hours go each week</h3>
          <p className="text-sm text-slate-500 mb-6">Time-and-motion estimates from oncology care teams running 12-20 active chemo patients.</p>
          <div className="space-y-3">
            <Bar label="Scheduling & calendar reconciliation"   hrs={6.0} max={8} color="bg-emerald-500" />
            <Bar label="Patient call-backs & appointment chase" hrs={5.0} max={8} color="bg-amber-500" />
            <Bar label="Side-effect log review & follow-up"     hrs={4.0} max={8} color="bg-violet-500" />
            <Bar label="Dictation & post-visit notes"           hrs={4.0} max={8} color="bg-indigo-500" />
            <Bar label="Reschedule/refill request triage"       hrs={3.0} max={8} color="bg-slate-400" />
          </div>
        </div>
      </section>

      {/* Interactive Build-your-own */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 md:px-12 pb-20">
        <div className="glass-card p-7 fade-up">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h3 className="font-bold text-lg mb-1">Build your own business case</h3>
              <p className="text-sm text-slate-500">Tune the three numbers for your oncology practice. Savings recalculate live.</p>
            </div>
            <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-500 font-bold">Interactive · screenshottable</span>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <Slider label="Hours saved per team per week" value={hours} min={4} max={40} unit="hrs" onChange={setHours} colorClass="accent-emerald-500" testid="roi-hours" />
            <Slider label="Blended clinician + admin rate" value={rate}  min={75} max={400} unit="/hr" prefix="$" onChange={setRate} colorClass="accent-amber-500" testid="roi-rate" />
            <Slider label="Oncology care teams"            value={teams} min={1}  max={20}  unit="teams" onChange={setTeams} colorClass="accent-violet-500" testid="roi-teams" />
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <ResultCard color="emerald" icon={Clock}      label="Annual hours saved" value={calc.annualHours.toLocaleString()} unit="hrs / yr" testid="roi-result-hours" />
            <ResultCard color="amber"   icon={DollarSign} label="Annual savings"      value={"$" + Math.round(calc.annualSavings).toLocaleString()} testid="roi-result-savings" />
            <ResultCard color="violet"  icon={Calendar}   label="Payback period"      value={calc.payback.toFixed(1)} unit="months" testid="roi-result-payback" />
          </div>
          <p className="text-center text-xs text-slate-500 mt-6">Based on a $100,000 one-time licence. Add applicable taxes per jurisdiction.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 sm:px-8 md:px-12 pb-20">
        <div className="glass-card p-10 text-center">
          <Activity className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold">Tailored pricing for multi-site networks.</h2>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto">Running OncoCadence across 5+ oncology clinics? We offer volume tiers and network-wide deployment. Book a call and we&apos;ll quote on the spot.</p>
          <Link to="/demo" className="inline-flex items-center gap-2 mt-7 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B1220] font-bold transition-colors">
            Talk to sales <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function InstallmentCard({ months, monthly, highlight }) {
  return (
    <div className={`glass-card p-6 ${highlight ? "border-emerald-500/40 ring-1 ring-emerald-500/20" : ""}`} data-testid={`installment-${months}`}>
      {highlight && <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-bold mb-2">Most popular</div>}
      <div className="text-3xl font-bold tabular-nums">${monthly.toLocaleString()}</div>
      <div className="text-sm text-slate-400 mt-1">per month × {months} months</div>
      <div className="text-xs text-slate-500 mt-4 leading-relaxed">First installment unlocks production access. Subsequent payments billed monthly via ACH or wire.</div>
    </div>
  );
}

function KPICard({ color, label, value, unit, caption }) {
  const map = { emerald: "text-emerald-400", amber: "text-amber-400", violet: "text-violet-400" };
  return (
    <div className="glass-card p-6">
      <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">{label}</div>
      <div className="flex items-baseline gap-2 mb-2">
        <div className={`text-4xl font-bold tabular-nums ${map[color]}`}>{value}</div>
        <div className="text-sm text-slate-400">{unit}</div>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">{caption}</p>
    </div>
  );
}

function Bar({ label, hrs, max, color }) {
  const pct = Math.min(100, (hrs / max) * 100);
  return (
    <div className="grid grid-cols-[1fr_2fr_auto] items-center gap-4 text-sm">
      <div className="text-slate-300">{label}</div>
      <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-slate-400 tabular-nums w-14 text-right">{hrs.toFixed(1)} hrs</div>
    </div>
  );
}

function Slider({ label, value, min, max, onChange, unit, prefix = "", colorClass, testid }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">{label}</div>
      <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} className={`w-full ${colorClass}`} data-testid={testid} />
      <div className="flex items-center justify-between text-xs text-slate-500 mt-1.5 tabular-nums">
        <span>{prefix}{min}</span>
        <span className="text-base text-white font-bold">{prefix}{value} {unit}</span>
        <span>{prefix}{max}</span>
      </div>
    </div>
  );
}

function ResultCard({ color, icon: Icon, label, value, unit, testid }) {
  const map = { emerald: "text-emerald-400", amber: "text-amber-400", violet: "text-violet-400" };
  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${map[color]}`} />
        <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">{label}</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className={`text-3xl font-bold tabular-nums ${map[color]}`}>{value}</span>
        {unit && <span className="text-sm text-slate-400">{unit}</span>}
      </div>
    </div>
  );
}
